import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { dedupeProfileQuestions, fillEquivalentAnswers } from '@/lib/profileQuestionDedup';
import { attributeValueCountsAsAnswer, extractAnswerValue } from '@/lib/answerShapes';
import {
  AGE_RANGE_QUESTION_ID,
  EQUIVALENCE_FLAT_KEYS,
  EQUIVALENT_ANSWERS_FLAG_KEY,
  fetchEquivalentAnswersEnabled,
  type EquivalenceSnapshot,
} from '@/lib/questionEquivalence';
import { applyProfileGating, type ProfileAnswerValue } from '@/lib/profileQuestionGating';
import { mapOptionLabelToId, toAgeRangeOptionPairs } from '@/lib/profileQuestionOptions';

/**
 * Single source of truth for profile completion percentage.
 *
 * Calculates: answered questions / questions the wizard actually asks.
 * - Total = active rows in question_catalog (GLOBAL + user country),
 *   de-duplicated the SAME way the wizard de-dups them (REK-1869). Counting the
 *   raw row total instead made 100% unreachable, because the wizard hides
 *   duplicate-keyed questions that the denominator still counted.
 * - Answered = how many of those de-duplicated (shown) questions have a
 *   user_knowledge `survey_qm_<id>` answer row (excluding skipped). Keying off
 *   the shown set means "answered everything you were shown" == 100%.
 */
export function useProfileProgress() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['profile-progress', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const userId = user!.id;

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('country')
        .eq('id', userId)
        .single();

      if (profileError) {
        const isAbort = profileError.message?.includes('AbortError') || profileError.message?.includes('signal is aborted');
        if (!isAbort) console.error('useProfileProgress: failed to load profile', profileError);
        throw profileError;
      }

      const rawCountry = profile?.country?.toUpperCase().trim() || null;
      const userCountry = rawCountry && /^[A-Z]{2}$/.test(rawCountry) ? rawCountry : null;

      // Fetch the rows (not just a count) so we can de-dup them exactly like the
      // wizard does. Order by priority_score DESC so the surviving row per key is
      // the same one the wizard renders.
      let qsmQuery = supabase
        .from('question_catalog')
        .select('id, profile_attribute_key, question_text, options, depends_on_question_id, depends_on_values')
        .eq('is_active', true)
        // priority_score is the wizard's display order; add a stable `id`
        // tie-breaker so de-dup picks the SAME surviving row per key as the
        // wizard query, even when scores tie (otherwise the shown vs counted
        // question could diverge for a key and stall completion).
        .order('priority_score', { ascending: false })
        .order('id', { ascending: true });

      if (userCountry) {
        qsmQuery = qsmQuery.or(`country.eq.GLOBAL,country.eq.${userCountry}`);
      } else {
        qsmQuery = qsmQuery.eq('country', 'GLOBAL');
      }

      const [qsmResult, ukResult] = await Promise.all([
        qsmQuery,
        supabase
          .from('user_knowledge')
          .select('attribute_key, attribute_value')
          .eq('user_id', userId)
          .like('attribute_key', 'survey_qm_%'),
      ]);

      if (qsmResult.error) throw qsmResult.error;
      if (ukResult.error) throw ukResult.error;

      // Map of questionMappingId -> stored answer. Flag OFF (default —
      // main behaviour): genuinely answered means selection present, not
      // skipped. Flag ON: any non-empty prod shape (a–e) counts.
      // attribute_key is `survey_qm_<id>`. Used both to detect answered
      // questions and to evaluate gating dependencies (which key off the
      // PARENT question's answer value).
      const equivalenceOn = await fetchEquivalentAnswersEnabled(() =>
        supabase
          .from('feature_flags')
          .select('enabled')
          .eq('flag_key', EQUIVALENT_ANSWERS_FLAG_KEY)
          .maybeSingle(),
      ).catch(() => false);
      const answerValuesById: Record<string, ProfileAnswerValue> = {};
      (ukResult.data || []).forEach((row) => {
        if (!equivalenceOn) {
          const val = row.attribute_value as { skipped?: boolean; selection?: ProfileAnswerValue };
          if (val && !val.skipped && val.selection !== undefined) {
            answerValuesById[row.attribute_key.replace('survey_qm_', '')] = val.selection;
          }
          return;
        }
        const val = row.attribute_value as { skipped?: boolean; selection?: ProfileAnswerValue };
        if (val && typeof val === 'object' && !Array.isArray(val) && val.skipped) return;
        if (!attributeValueCountsAsAnswer(row.attribute_value)) return;
        const extracted = extractAnswerValue(row.attribute_value);
        if (typeof extracted === 'string' || Array.isArray(extracted)) {
          answerValuesById[row.attribute_key.replace('survey_qm_', '')] = extracted as ProfileAnswerValue;
        } else {
          // Non-empty non-string carrier (e.g. a finite number): counts as
          // answered for the denominator without driving string gating.
          answerValuesById[row.attribute_key.replace('survey_qm_', '')] =
            String(extracted) as ProfileAnswerValue;
        }
      });
      const answeredIds = new Set<string>(Object.keys(answerValuesById));

      // Equivalence fills (flag-on — mirrors the wizard's applyProfileData
      // fill so "shown" == "counted" under the flag): age range counts when
      // DOB is known, zip counts when postal_code is known, and unlisted
      // duplicate ids count through the generic profile-key rule.
      // Fail-closed: any flat/profile read error keeps exact-id behaviour.
      try {
        if (equivalenceOn) {
          const [{ data: flatRows }, { data: contact }] = await Promise.all([
            supabase
              .from('user_knowledge')
              .select('attribute_key, attribute_value')
              .eq('user_id', userId)
              .in('attribute_key', [...EQUIVALENCE_FLAT_KEYS]),
            supabase.from('profiles').select('email, phone_number').eq('id', userId).single(),
          ]);
          const flatAnswersByKey = new Map<string, unknown>();
          const flatKeys = new Set<string>();
          for (const row of flatRows ?? []) {
            const raw = (row as { attribute_value?: unknown }).attribute_value;
            if (!attributeValueCountsAsAnswer(raw)) continue;
            const key = (row as { attribute_key?: string }).attribute_key;
            if (typeof key !== 'string' || key.length === 0) continue;
            flatKeys.add(key);
            flatAnswersByKey.set(key, extractAnswerValue(raw));
          }
          const contactEmail = (contact as { email?: unknown } | null)?.email;
          const contactPhone = (contact as { phone_number?: unknown } | null)?.phone_number;
          const snapshot: EquivalenceSnapshot = {
            answeredIds,
            flatKeys,
            hasProfileEmail: typeof contactEmail === 'string' && contactEmail.trim().length > 0,
            hasProfilePhone: typeof contactPhone === 'string' && contactPhone.trim().length > 0,
          };
          const answersById = new Map<string, unknown>(Object.entries(answerValuesById));
          // Age-range options as `{ id, text }` pairs (mirrors the wizard
          // fill): the DOB derivation resolves to the stored-form option id,
          // and the shared label→id backstop below stores the identical
          // value the wizard stores for the same member (R2-1). Without the
          // options a raw DOB would poison age-gated counting.
          const ageRow = (qsmResult.data || []).find((q) => q.id === AGE_RANGE_QUESTION_ID) as
            | { options?: unknown }
            | undefined;
          const ageRangeOptions = toAgeRangeOptionPairs(ageRow?.options);
          // Generic-match catalog (contract v3 `matchBy`): the full qsm
          // universe already carries every id → key, plus a fill for
          // answered ids (e.g. inactive questions) it does not cover.
          const questionProfileKeys = new Map<string, string | null>(
            ((qsmResult.data || []) as Array<{ id: string; profile_attribute_key?: unknown }>).map(
              (q) => [q.id, typeof q.profile_attribute_key === 'string' ? q.profile_attribute_key : null] as const,
            ),
          );
          const uncoveredProgressIds = [...answeredIds].filter((id) => !questionProfileKeys.has(id));
          if (uncoveredProgressIds.length > 0) {
            const { data: progressFill } = await supabase
              .from('question_catalog')
              .select('id, profile_attribute_key')
              .in('id', uncoveredProgressIds);
            for (const row of (progressFill ?? []) as Array<{ id: string; profile_attribute_key?: unknown }>) {
              if (typeof row.id === 'string' && !questionProfileKeys.has(row.id)) {
                questionProfileKeys.set(row.id, typeof row.profile_attribute_key === 'string' ? row.profile_attribute_key : null);
              }
            }
          }
          const filled = fillEquivalentAnswers(
            (qsmResult.data || []).map((q) => q.id),
            snapshot,
            answersById,
            flatAnswersByKey,
            ageRangeOptions,
            { questionProfileKeys },
          );
          for (const [id, value] of filled) {
            // Same shared helper as the wizard fill: a derived age label
            // stores the catalog option id when options carry explicit ids,
            // else the label verbatim — so "shown" == "counted" even then.
            const stored =
              id === AGE_RANGE_QUESTION_ID && typeof value === 'string'
                ? mapOptionLabelToId(ageRow?.options, value)
                : value;
            answerValuesById[id] =
              typeof stored === 'string' || Array.isArray(stored)
                ? (stored as ProfileAnswerValue)
                : (String(stored) as ProfileAnswerValue);
            answeredIds.add(id);
          }
        }
      } catch {
        // Equivalence fill failed — exact-id behaviour kept.
      }

      // The set of questions the wizard actually presents: de-dup (REK-1869)
      // THEN apply conditional gating (REK-1916) using the user's answers, so
      // gated-out questions (e.g. child questions for a non-parent) are excluded
      // from BOTH the denominator and the answered count. Both surfaces apply the
      // same dedupe+gate so "shown" == "counted".
      const shownQuestions = applyProfileGating(
        dedupeProfileQuestions(qsmResult.data || []),
        (questionId) => answerValuesById[questionId],
        equivalenceOn,
      );
      const totalQuestions = shownQuestions.length;

      // Count only answers to questions that are actually shown, so completion
      // can reach 100% by answering everything the wizard asks.
      const answeredCount = shownQuestions.filter((q) => answeredIds.has(q.id)).length;

      const percentage = totalQuestions > 0
        ? Math.min(100, Math.round((answeredCount / totalQuestions) * 100))
        : 0;

      return {
        percentage,
        answeredCount,
        totalQuestions,
        isComplete: answeredCount >= totalQuestions && totalQuestions > 0,
      };
    },
    staleTime: 30_000,
  });
}
